// Rush Flurry strike sounds: original procedural WebAudio synthesis (no samples, no third-party audio).
// Replaces the MyInstants clips rush_hit_1..3 / rush_kick (2026-11-02, public-release audio clean-up; provenance in
// assets/audio/shatterlight/candidates/README.md). Timing and level follow the clips they replace: punches ~0.3 s, the
// kick ~0.42 s, the impact within the first 5-25 ms, peak about -2 dBFS before the caller's gain (0.42 punch / 0.5 kick).
//
// A punch is four layers: a pitch-dropping sine THUMP (through a soft saturator so it reads on small speakers), a short
// triangle PUNCH an octave up for weight, a bandpassed noise SLAP plus a 10 ms high-passed CRACK for the leading edge, and a
// low-passed noise FLESH body that darkens as it decays. The kick is lower and longer, with a sub layer and a WHOOSH: a
// rising swish into the impact and a falling follow-through behind it. Every strike gets a small random pitch detune and a
// random start inside the noise buffer, so a long flurry never repeats exactly.

export type RushKind = 'hit1' | 'hit2' | 'hit3' | 'kick';
export const RUSH_KINDS: readonly RushKind[] = ['hit1', 'hit2', 'hit3', 'kick'];
/** how long a strike sounds, seconds (the voice cap in Sfx keeps a flurry from stacking past this) */
export const RUSH_DUR: Record<RushKind, number> = { hit1: 0.3, hit2: 0.3, hit3: 0.32, kick: 0.44 };

interface Spec {
  /** thump: start / end frequency (Hz), sweep time and decay (s), level */
  f0: number; f1: number; sweep: number; decay: number; thump: number;
  /** triangle punch an octave up: level and decay */
  punch: number; punchDec: number;
  /** noise slap: band centre (Hz), Q, level, decay */
  slapF: number; slapQ: number; slap: number; slapDec: number;
  /** 10 ms high-passed crack at the very front */
  crack: number;
  /** low-passed noise body: start cutoff, level, decay */
  fleshF: number; flesh: number; fleshDec: number;
  /** optional second, softer knock a few ms later (layered feel) */
  echoAt?: number; echo?: number;
  /** optional sub layer: frequency, level, decay */
  sub?: { f: number; level: number; decay: number };
  /** optional whoosh around the impact */
  whoosh?: { lead: number; leadAmp: number; tail: number; tailAmp: number; f0: number; f1: number; f2: number };
  /** where the impact lands inside the sound, s */
  at: number;
  /** overall trim, calibrated so the dry peak is about -2 dBFS */
  trim: number;
}

const SPECS: Record<RushKind, Spec> = {
  // jab: light, fast, bright slap
  hit1: { f0: 200, f1: 70, sweep: 0.05, decay: 0.27, thump: 0.62, punch: 0.3, punchDec: 0.08, slapF: 3200, slapQ: 0.9, slap: 0.5, slapDec: 0.06, crack: 0.22, fleshF: 1500, flesh: 0.34, fleshDec: 0.27, at: 0.004, trim: 1.25 },
  // cross: heavier, a little lower, a soft second knock (body roll-through)
  hit2: { f0: 168, f1: 58, sweep: 0.06, decay: 0.3, thump: 0.7, punch: 0.34, punchDec: 0.09, slapF: 2500, slapQ: 0.8, slap: 0.5, slapDec: 0.07, crack: 0.2, fleshF: 1300, flesh: 0.38, fleshDec: 0.3, echoAt: 0.034, echo: 0.3, at: 0.004, trim: 1.25 },
  // hook: lowest of the three, dull and weighty
  hit3: { f0: 146, f1: 50, sweep: 0.07, decay: 0.34, thump: 0.78, punch: 0.34, punchDec: 0.1, slapF: 1900, slapQ: 0.7, slap: 0.46, slapDec: 0.08, crack: 0.16, fleshF: 1100, flesh: 0.44, fleshDec: 0.34, echoAt: 0.026, echo: 0.22, at: 0.004, trim: 1.25 },
  // kick: heaviest; sub + whoosh, impact ~50 ms in
  kick: {
    f0: 128, f1: 36, sweep: 0.12, decay: 0.5, thump: 0.8, punch: 0.4, punchDec: 0.1, slapF: 1700, slapQ: 0.7, slap: 0.55, slapDec: 0.09, crack: 0.18,
    fleshF: 900, flesh: 0.5, fleshDec: 0.4, sub: { f: 46, level: 0.55, decay: 0.5 },
    whoosh: { lead: 0.05, leadAmp: 0.22, tail: 0.3, tailAmp: 0.3, f0: 380, f1: 2600, f2: 500 }, at: 0.05, trim: 0.85,
  },
};

let satCurve: Float32Array<ArrayBuffer> | null = null;
function saturator(): Float32Array<ArrayBuffer> {
  if (!satCurve) { satCurve = new Float32Array(1024); const k = 2.2, n = Math.tanh(k); for (let i = 0; i < 1024; i++) satCurve[i] = Math.tanh(k * (i / 511.5 - 1)) / n; }
  return satCurve;
}

/** soft clipper: unity-ish below 0.4, saturates smoothly to 0.79 (about -2 dBFS) */
let clipC: Float32Array<ArrayBuffer> | null = null;
function clipCurve(): Float32Array<ArrayBuffer> {
  if (!clipC) { clipC = new Float32Array(2048); for (let i = 0; i < 2048; i++) clipC[i] = 0.79 * Math.tanh(1.6 * (i / 1023.5 - 1)) / Math.tanh(1.6); }
  return clipC;
}

export interface RushOpts {
  /** start time (AudioContext seconds); default now */
  at?: number;
  /** linear level before the caller's mix (1 = the sound's own -2 dBFS peak) */
  gain?: number;
  /** reverb-send level */
  wet?: number;
  /** pitch multiplier, e.g. 0.98..1.02 from the caller */
  rate?: number;
  /** random source 0..1 (seedable for QA); default Math.random */
  rnd?: () => number;
}

/** Play one Rush Flurry strike. `dest` is the SFX bus, `send` the shared reverb send, `noise` a mono white-noise buffer (>= 1 s). */
export function playRush(c: BaseAudioContext, dest: AudioNode, send: AudioNode | null, noise: AudioBuffer, kind: RushKind, o: RushOpts = {}): void {
  const s = SPECS[kind], rnd = o.rnd ?? Math.random, t0 = o.at ?? c.currentTime, t = t0 + s.at;
  const det = (o.rate ?? 1) * (0.95 + rnd() * 0.1); // slight pitch variation per strike
  const dur = RUSH_DUR[kind], end = t0 + dur;
  // layers -> bus (drive) -> soft clipper (the peak can never pass ~-2 dBFS, whatever the random noise does) -> out
  const bus = c.createGain(); bus.gain.value = s.trim;
  const clip = c.createWaveShaper(); clip.curve = clipCurve(); clip.oversample = '2x';
  const out = c.createGain(); out.gain.setValueAtTime(o.gain ?? 1, t0); out.gain.setValueAtTime(o.gain ?? 1, end - 0.05); out.gain.linearRampToValueAtTime(0, end); // closes the tail to silence
  bus.connect(clip).connect(out).connect(dest);
  if (o.wet && send) { const w = c.createGain(); w.gain.value = o.wet; out.connect(w).connect(send); }
  // amplitude envelope: ~1.2 ms attack (no click), then exponential decay
  const env = (g: GainNode, at: number, peak: number, dec: number, atk = 0.0012) => {
    g.gain.setValueAtTime(0.0001, at); g.gain.linearRampToValueAtTime(peak, at + atk); g.gain.exponentialRampToValueAtTime(0.0001, at + atk + dec);
  };
  const osc = (type: OscillatorType, f0: number, f1: number, sweep: number, at: number, peak: number, dec: number, shape: boolean) => {
    const o1 = c.createOscillator(), g = c.createGain();
    o1.type = type; o1.frequency.setValueAtTime(f0 * det, at); o1.frequency.exponentialRampToValueAtTime(Math.max(20, f1 * det), at + sweep);
    env(g, at, peak, dec);
    if (shape) { const sh = c.createWaveShaper(); sh.curve = saturator(); sh.oversample = '2x'; o1.connect(sh).connect(g); } else o1.connect(g);
    g.connect(bus); o1.start(at); o1.stop(Math.min(at + 0.0012 + dec + 0.02, end + 0.02));
  };
  const nz = (at: number, len: number) => { // noise burst source starting at a random point of the buffer
    const src = c.createBufferSource(); src.buffer = noise; src.loop = true;
    src.start(at, rnd() * Math.max(0.1, noise.duration - len - 0.05)); src.stop(at + len + 0.02);
    return src;
  };
  // 1. thump (+ optional second knock, + sub)
  osc('sine', s.f0, s.f1, s.sweep, t, s.thump, s.decay, true);
  if (s.echoAt && s.echo) osc('sine', s.f0 * 0.8, s.f1, s.sweep, t + s.echoAt, s.echo, s.decay * 0.7, true);
  if (s.sub) osc('sine', s.sub.f, s.sub.f * 0.7, s.sub.decay, t, s.sub.level, s.sub.decay, false);
  // 2. punch: triangle an octave up, short
  osc('triangle', s.f0 * 2.05, s.f1 * 2.6, s.sweep * 0.8, t, s.punch, s.punchDec, false);
  // 3. slap: bandpassed noise + a high-passed crack
  {
    const len = s.slapDec * 2.2, src = nz(t, len), f = c.createBiquadFilter(), g = c.createGain();
    f.type = 'bandpass'; f.frequency.value = s.slapF * det; f.Q.value = s.slapQ; env(g, t, s.slap, s.slapDec, 0.0025);
    src.connect(f).connect(g).connect(bus);
    const s2 = nz(t, 0.02), h = c.createBiquadFilter(), g2 = c.createGain();
    h.type = 'bandpass'; h.frequency.value = 3800; h.Q.value = 0.8; const h2 = c.createBiquadFilter(); h2.type = 'lowpass'; h2.frequency.value = 6500; env(g2, t, s.crack, 0.016, 0.0025); s2.connect(h).connect(h2).connect(g2).connect(bus);
  }
  // 4. flesh: low-passed noise that closes as it decays
  {
    const len = s.fleshDec * 2.2, src = nz(t, len), f = c.createBiquadFilter(), g = c.createGain();
    f.type = 'lowpass'; f.Q.value = 0.7; f.frequency.setValueAtTime(s.fleshF * det, t); f.frequency.exponentialRampToValueAtTime(170, t + s.fleshDec);
    env(g, t, s.flesh, s.fleshDec); src.connect(f).connect(g).connect(bus);
  }
  // 5. kick whoosh: rising swish into the impact, falling follow-through behind it
  if (s.whoosh) {
    const w = s.whoosh;
    const lead = nz(t0, w.lead + 0.02), lf = c.createBiquadFilter(), lg = c.createGain();
    lf.type = 'bandpass'; lf.Q.value = 1.3; lf.frequency.setValueAtTime(w.f0 * det, t0); lf.frequency.exponentialRampToValueAtTime(w.f1 * det, t);
    lg.gain.setValueAtTime(0.0001, t0); lg.gain.exponentialRampToValueAtTime(w.leadAmp, t - 0.004); lg.gain.exponentialRampToValueAtTime(0.0001, t + 0.008);
    lead.connect(lf).connect(lg).connect(bus);
    const tail = nz(t, w.tail), tf = c.createBiquadFilter(), tg = c.createGain();
    tf.type = 'bandpass'; tf.Q.value = 0.9; tf.frequency.setValueAtTime(w.f1 * 0.9 * det, t); tf.frequency.exponentialRampToValueAtTime(w.f2 * det, t + w.tail);
    tg.gain.setValueAtTime(0.0001, t); tg.gain.linearRampToValueAtTime(w.tailAmp, t + 0.02); tg.gain.exponentialRampToValueAtTime(0.0001, Math.min(end, t + w.tail));
    tail.connect(tf).connect(tg).connect(bus);
  }
}
